import { AutocompleteProps as MuiAutocompleteProps } from '@mui/material';
import { ChipConfig } from '../../_components/ChipList/chipList.types';
import { withLockedPreserved } from '../../_components/ChipList/chipList.utils';

interface UseLockedSelectionParams<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> {
  value: MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['value'];
  multiple?: Multiple;
  isOptionEqualToValue?: (a: T, b: T) => boolean;
  getChipConfig?: (value: T) => ChipConfig | undefined;
  onChange?: MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['onChange'];
}

const useLockedSelection = <
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>({
  value,
  multiple,
  isOptionEqualToValue,
  getChipConfig,
  onChange,
}: UseLockedSelectionParams<T, Multiple, DisableClearable, FreeSolo>) => {
  const isValueLocked = (option: T) => getChipConfig?.(option)?.isDeletable === false;

  const isSelected = (option: T) =>
    Array.isArray(value) &&
    value.some((selected) =>
      isOptionEqualToValue ? isOptionEqualToValue(selected, option) : selected === option,
    );

  const getOptionDisabled =
    multiple && getChipConfig
      ? (option: T) => isValueLocked(option) && isSelected(option)
      : undefined;

  const handleChange: MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['onChange'] = (
    event,
    nextValue,
    reason,
    details,
  ) => {
    if (multiple && getChipConfig && Array.isArray(nextValue)) {
      const preserved = withLockedPreserved({
        current: (value as T[] | undefined) ?? [],
        next: nextValue as T[],
        isLocked: isValueLocked,
        isEqual: isOptionEqualToValue,
      });
      onChange?.(event, preserved as typeof nextValue, reason, details);
      return;
    }

    onChange?.(event, nextValue, reason, details);
  };

  return { getOptionDisabled, handleChange };
};

export default useLockedSelection;
